import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
  convertFromHTML,
} from 'draft-js'
import dynamic from 'next/dynamic'
import { getDefaultValidations, validate } from '../Validations'
import { useTheme } from '@libs/useTheme'
import Box from '@components/base/Box'
import { BaseInputProps } from '../BaseInputProps'
import Resizeable from '@libs/Utility/resizeable'
import Tooltip from '@components/tootilp/Tooltip'
import { debounce, isJSON } from '@libs/Utility/util'
import { useRouter } from 'next/router'
import draftToHtml from 'draftjs-to-html'
import {
  DEFAULT_EDITOR_FONT_FAMILY,
  EditorFullCustomDecorators,
  FontFamilyDropdown,
  FontSizeDropdown,
  PlaceholderButton,
} from './TextEditorUtil'

const DynamicEditor = dynamic<any>(
  () => import('react-draft-wysiwyg').then((mod) => mod.Editor as any),
  { ssr: false }
)

const RichTextEditor: React.FC<
  BaseInputProps & {
    setError?: (message: string, testId: string) => void
    htmlOutput?: boolean
    resizeEnabled?: boolean
    decorators?: any[]
  }
> = ({
  label,
  placeholder,
  className,
  validations,
  required,
  hint,
  testid,
  onChange,
  value,
  show = true,
  textColour,
  labelBgColour,
  tooltip,
  setError,
  htmlOutput = false,
  resizeEnabled = true,
  decorators,
}) => {
  // console.log({ value })
  const { theme } = useTheme()
  const { isReady } = useRouter()
  const [error, setLocalError] = useState('')

  const [showTooltip, setShowTooltip] = useState(true)
  useEffect(() => {
    // Autohide tooltip if the input is not required.
    if (tooltip?.toLowerCase().includes('require') && !required)
      setShowTooltip(false)
  }, [tooltip, required])

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  )
  const editorStateRef = useRef(editorState)
  editorStateRef.current = editorState

  useEffect(() => {
    // console.log({ value, valueType: typeof value, window: !!window, isReady })
    if (!isReady) return
    if (value && typeof value === 'string' && value.trim() !== '' && !!window) {
      try {
        if (htmlOutput && !isJSON(value)) {
          console.log('HTML', { value })
          const rawContentFromProps = convertFromHTML(value)
          const contentState = ContentState.createFromBlockArray(
            rawContentFromProps.contentBlocks,
            rawContentFromProps.entityMap
          )
          setEditorState(EditorState.createWithContent(contentState))
        } else {
          console.log('Not HTML', { value })
          const rawContentFromProps = JSON.parse(value)
          const contentState = convertFromRaw(rawContentFromProps)
          setEditorState(EditorState.createWithContent(contentState))
        }
      } catch (error) {
        console.error(
          'Error to convert the initial value to EditorState: ',
          error
        )
      }
    }
  }, [isReady, window])

  const handleChange = useCallback(
    debounce((val: string) => {
      if (onChange) {
        onChange(val)
      }
    }, 1000),
    []
  )

  if (!show) return <></>

  const parsedValidations = getDefaultValidations(undefined, validations)

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState)

    const plainText = newEditorState.getCurrentContent().getPlainText('')

    const errorMessage = validate(plainText, parsedValidations)

    if (!!setError) setError(errorMessage, testid ?? label ?? 'unknown')

    setLocalError(errorMessage)

    if (!errorMessage) {
      const rawContentState = convertToRaw(newEditorState.getCurrentContent())
      let payload

      htmlOutput
        ? (payload = {
            data: JSON.stringify(rawContentState),
            markup: draftToHtml(rawContentState),
          })
        : (payload = JSON.stringify(rawContentState))

      handleChange(payload)
    }
  }

  const isDarkTheme = theme.type === 'DARK'

  // The toolbar does not pass EditorState to custom controls, so inject the
  // current state to let the size control report H1/H2/H3 accurately.
  const FontSizeControl = (props: any) => (
    <FontSizeDropdown {...props} editorState={editorStateRef.current} />
  )

  // const handleKeyCommand = (cmd: string): DraftHandleValue => {
  //   console.log({ cmd })
  //   if (cmd === 'init-autocomplete') {
  //     return 'handled'
  //   }
  //   return 'not-handled'
  // }
  //

  // console.log({ decorators })

  return (
    <label
      className={['flex flex-col my-3', className].join(' ')}
      style={{ color: textColour }}
    >
      {label && (
        <span
          className={`mb-1.5 text-sm font-medium leading-5 transition-all ${
            error ? 'text-red-500' : ''
          }`}
        >
          <Tooltip
            show={showTooltip}
            tooltip={tooltip}
            className={`flex flex-col`}
          >
            <span
              className={`z-10 ${
                required
                  ? "after:content-['*'] after:ml-1 after:text-red-500"
                  : ''
              }`}
            >
              {label}
            </span>
          </Tooltip>
        </span>
      )}
      {typeof window === 'object' && (
        <Resizeable
          enabled={resizeEnabled}
          className={resizeEnabled ? '' : className}
          initalSize={{ height: 400 }}
          minHeight={120}
        >
          <DynamicEditor
            spellCheck
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            wrapperClassName={`${resizeEnabled ? 'size-full h-full overflow-hidden' : 'w-full flex-1'} compact-rich-editor flex flex-col !box-border`}
            /* @ts-ignore */
            // keyBindingFn={keyBindingFn}
            // handleKeyCommand={handleKeyCommand}
            editorClassName={`my-rich-editor compact-rich-editor__content ${
              error ? 'text-red-500 border-red-500' : ''
            }`}
            toolbarCustomButtons={
              htmlOutput
                ? [
                    <PlaceholderButton
                      key="placeholderButton"
                      editorState={editorState}
                      onChange={setEditorState}
                      type={decorators ? 'membership' : undefined}
                    />,
                  ]
                : []
            }
            toolbarClassName={`compact-rich-editor__toolbar flex flex-wrap justify-start gap-1 p-1.5 border-b border-gray-300 ${
              isDarkTheme ? 'text-blue-700 ' : 'text-black'
            }`}
            toolbar={{
              options: [
                'fontFamily',
                'fontSize',
                'blockType',
                'inline',
                'list',
                'link',
                'image',
                'colorPicker',
                'remove',
                'history',
              ],
              fontFamily: {
                component: FontFamilyDropdown,
                options: [
                  DEFAULT_EDITOR_FONT_FAMILY,
                  'Arial',
                  'Georgia',
                  'Impact',
                  'Tahoma',
                  'Times New Roman',
                  'Verdana',
                ],
              },
              fontSize: {
                component: FontSizeControl,
                options: [
                  8,
                  9,
                  10,
                  11,
                  12,
                  14,
                  16,
                  18,
                  24,
                  30,
                  36,
                  40,
                ],
              },
              blockType: {
                inDropdown: true,
                options: ['Normal', 'H1', 'H2', 'H3'],
              },
              inline: {
                options: [
                  'bold',
                  'italic',
                  'underline',
                  'strikethrough',
                ],
                bold: {},
                italic: {},
                underline: {},
                strikethrough: {},
              },
              list: {
                options: ['unordered', 'ordered'],
                unordered: {},
                ordered: {},
              },

              link: {
                inDropdown: false,
                className: undefined,
                component: undefined,
                popupClassName: undefined,
                dropdownClassName: undefined,
                showOpenOptionOnHover: true,
                defaultTargetOption: '_self',
                options: ['link', 'unlink'],
                linkCallback: undefined,
              },
              image: {
                alt: { present: true, mandatory: false },
                defaultSize: {
                  height: 'auto',
                  width: 'auto',
                },
                uploadEnabled: false,
                inputAccept: 'image/*',
                previewImage: false,
              },

              remove: {},
              history: {
                options: ['undo', 'redo'],
                undo: {},
                redo: {},
              },
            }}
            customDecorators={decorators ?? EditorFullCustomDecorators}
            placeholder={placeholder}
            data-testid={testid ?? undefined}
          />
        </Resizeable>
      )}
      {hint && <span className="mt-1 mb-4 text-xs">{hint}</span>}
      <Box
        ignoreTheme
        className={`mt-1 transition-all ${
          error ? 'opacity-100 text-red-500' : 'opacity-0'
        }`}
      >
        {error || '-'}
      </Box>
    </label>
  )
}

export default RichTextEditor
